import { useEffect, useId, useState } from 'react';
import { cn } from '@/lib/utils';

interface Props {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  /** Renders the numeric inputs & bubble labels, e.g. (v) => "25.000 ₺". */
  format: (value: number) => string;
  /** Converts the raw input string back to the slider unit (e.g. ₺ → kuruş). */
  parseInput?: (raw: string) => number;
  /** Converts the slider unit to the input display value (e.g. kuruş → ₺). */
  toInput?: (value: number) => number;
  suffix?: string;
  minTestId: string;
  maxTestId: string;
  minSliderTestId: string;
  maxSliderTestId: string;
}

/**
 * Accessible dual-handle range slider built from two stacked native inputs.
 * Handles cannot cross: the min handle is clamped below the max and vice versa.
 */
export default function DualRangeSlider({
  label, min, max, step = 1, value, onChange, format,
  parseInput = Number, toInput = (v) => v, suffix,
  minTestId, maxTestId, minSliderTestId, maxSliderTestId,
}: Props) {
  const id = useId();
  const [local, setLocal] = useState<[number, number]>(value);

  useEffect(() => setLocal(value), [value[0], value[1]]);

  const commit = (next: [number, number]) => {
    setLocal(next);
    onChange(next);
  };

  const setMin = (raw: number) => {
    const clamped = Math.min(Math.max(raw, min), local[1]);
    commit([clamped, local[1]]);
  };
  const setMax = (raw: number) => {
    const clamped = Math.max(Math.min(raw, max), local[0]);
    commit([local[0], clamped]);
  };

  const pct = (v: number) => ((v - min) / (max - min)) * 100;

  return (
    <fieldset className="space-y-3">
      <legend className="yd-label">{label}</legend>

      {/* Track */}
      <div className="relative h-6 select-none">
        <div className="absolute top-2.5 h-1.5 w-full rounded-full bg-ink-200" />
        <div
          className="absolute top-2.5 h-1.5 rounded-full bg-bosphorus-600"
          style={{ left: `${pct(local[0])}%`, width: `${Math.max(pct(local[1]) - pct(local[0]), 0)}%` }}
        />
        <input
          type="range"
          className="yd-range top-2.5"
          min={min} max={max} step={step}
          value={local[0]}
          onChange={(e) => setMin(Number(e.target.value))}
          aria-label={`${label} en az`}
          aria-valuetext={format(local[0])}
          data-testid={minSliderTestId}
          id={`${id}-min`}
        />
        <input
          type="range"
          className="yd-range top-2.5"
          min={min} max={max} step={step}
          value={local[1]}
          onChange={(e) => setMax(Number(e.target.value))}
          aria-label={`${label} en fazla`}
          aria-valuetext={format(local[1])}
          data-testid={maxSliderTestId}
          id={`${id}-max`}
        />
      </div>

      {/* Numeric inputs */}
      <div className="flex items-center gap-2">
        <label className="flex-1">
          <span className="sr-only">{label} en az</span>
          <div className="relative">
            <input
              type="number"
              className={cn('yd-input pr-10 text-sm')}
              min={toInput(min)} max={toInput(max)}
              value={toInput(local[0])}
              onChange={(e) => setMin(parseInput(e.target.value || '0'))}
              data-testid={minTestId}
              data-raw-value={local[0]}
            />
            {suffix && <span className="pointer-events-none absolute right-3 top-2.5 text-xs text-ink-400">{suffix}</span>}
          </div>
        </label>
        <span className="text-ink-400" aria-hidden>–</span>
        <label className="flex-1">
          <span className="sr-only">{label} en fazla</span>
          <div className="relative">
            <input
              type="number"
              className={cn('yd-input pr-10 text-sm')}
              min={toInput(min)} max={toInput(max)}
              value={toInput(local[1])}
              onChange={(e) => setMax(parseInput(e.target.value || String(toInput(max))))}
              data-testid={maxTestId}
              data-raw-value={local[1]}
            />
            {suffix && <span className="pointer-events-none absolute right-3 top-2.5 text-xs text-ink-400">{suffix}</span>}
          </div>
        </label>
      </div>

      <p className="text-xs text-ink-500">
        {format(local[0])} – {format(local[1])}
      </p>
    </fieldset>
  );
}
